import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../../../../app/hooks";
import { EXCLUSIVITIES_PAGE_SIZE } from "../../../../../../common/components/functional/pagination/constants";
import { fetchCustomerCompanyByOrgId } from "../../../../../customer/customerOrganizationsSlice";
import { WorkLocation } from "../../../../../customer/types/queries";
import { TalentExclusivity } from "../../../../../talents/talentAPI";
import { fetchExclusivitiesByTalentId } from "../../../../../talents/talentsSlice";
import { isSelectionAllowedByExclusivities } from "./exclusivityMatching";

const useExclusivityConflict = (
  talentId: string,
  workLocation: WorkLocation | null,
  selectedWorkTitleIds: string[],
) => {
  const dispatch = useAppDispatch();
  const [exclusivities, setExclusivities] = useState<TalentExclusivity[]>([]);
  const [isExclusivitiesLoaded, setIsExclusivitiesLoaded] = useState(false);
  // null = not fetched yet, string = success, false = fetch failed
  const [resolvedCompanyId, setResolvedCompanyId] = useState<
    string | null | false
  >(null);
  const [isCompanyIdLoading, setIsCompanyIdLoading] = useState(false);

  useEffect(() => {
    dispatch(
      fetchExclusivitiesByTalentId({
        talentId,
        pageSize: EXCLUSIVITIES_PAGE_SIZE,
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) setExclusivities(action.payload.data);
      })
      .finally(() => setIsExclusivitiesLoaded(true));
  }, [talentId, dispatch]);

  useEffect(() => {
    setResolvedCompanyId(null);

    const companyOnlyExclusivities = exclusivities.filter(
      (ex) => !ex.customerOrganizationId && !ex.workLocationId,
    );

    if (
      !workLocation ||
      !isExclusivitiesLoaded ||
      companyOnlyExclusivities.length === 0
    ) {
      setIsCompanyIdLoading(false);
      return;
    }

    setIsCompanyIdLoading(true);
    dispatch(
      fetchCustomerCompanyByOrgId({
        customerOrgId: workLocation.customerOrgId,
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          setResolvedCompanyId(action.payload.customerCompanyId);
        } else {
          setResolvedCompanyId(false);
        }
      })
      .finally(() => setIsCompanyIdLoading(false));
  }, [workLocation, isExclusivitiesLoaded, exclusivities, dispatch]);

  const companyOnlyExclusivities = exclusivities.filter(
    (ex) => !ex.customerOrganizationId && !ex.workLocationId,
  );

  const needsCompanyResolution =
    isExclusivitiesLoaded &&
    companyOnlyExclusivities.length > 0 &&
    !!workLocation;

  const isValidatingExclusivity =
    !isExclusivitiesLoaded ||
    (needsCompanyResolution &&
      (isCompanyIdLoading || resolvedCompanyId === null));

  const isExclusivityConflict =
    !isValidatingExclusivity &&
    !!workLocation &&
    !isSelectionAllowedByExclusivities({
      exclusivities,
      workLocation,
      selectedWorkTitleIds,
      resolvedCompanyId:
        typeof resolvedCompanyId === "string" ? resolvedCompanyId : null,
    });

  const isExclusivityCheckFailed =
    needsCompanyResolution && resolvedCompanyId === false;

  return {
    isExclusivityConflict,
    isValidatingExclusivity,
    isExclusivityCheckFailed,
  };
};

export default useExclusivityConflict;
